import { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import FormField, {
    FormFieldClickable,
    FormFieldComponentProps,
    FormFieldIcon,
    FormFieldPresentationProps,
    FormFieldSizeVariant,
} from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AnimatedTextField from "@insite/mobius/TextField/AnimatedTextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { MobiusStyledComponentPropsWithRef } from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { HtmlAttributes } from "csstype";
import * as React from "react";
import styled, { ThemeConsumer } from "styled-components";

export interface TextFieldPresentationProps extends FormFieldPresentationProps<TextFieldComponentProps> {
    /** Props to be passed into the Clickable component that will optionally wrap the icon.
     * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
    iconClickableProps?: ClickablePresentationProps;
    leftIconClickableProps?: ClickablePresentationProps;

    /** The props for icon displaying on the text input field.
     * @themable */
    iconProps?: IconPresentationProps;
    leftIconProps?: IconPresentationProps;
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<TextFieldComponentProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** Background color of the form element */
    backgroundColor?: string;
    /** The props for the textfield label */
    labelProps?: TypographyPresentationProps;
    /** Boolean value for width animation */
    isAnimated?: boolean;
}

type TextFieldComponentProps = MobiusStyledComponentPropsWithRef<
    "input",
    { ref?: React.Ref<HTMLInputElement | null> },
    {
        /** The props for icon displaying on the text input field. */
        iconProps?: IconPresentationProps;
        /** Props to be passed into the Clickable component that will optionally wrap the icon.
         * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
        iconClickableProps?: ClickableProps;
        leftIconClickableProps?: ClickableProps;

        /** Visually hidden text to be rendered within the text field clickable. */
        clickableText?: string;
        /** Error message to be displayed below the input. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the input. */
        hint?: React.ReactNode;
        /** Label to be displayed above the input. */
        label?: React.ReactNode;
    } & Partial<FormFieldComponentProps>
>;

export type TextFieldProps = TextFieldComponentProps & TextFieldPresentationProps;

export const InputStyle = styled.input<InjectableCss>`
    ${injectCss}
`;

const omitKeys = [
    "sizeVariant",
    "border",
    "label",
    "labelPosition",
    "theme",
    "cssOverrides",
    "iconProps",
    "leftIconProps",
    "backgroundColor",
    "labelProps",
    "dispatch",
    "labelId",
    "isAnimated",
    "aria-expanded",
    "aria-controls",
] as (keyof Omit<
    TextFieldProps,
    | "id"
    | "type"
    | "onKeyDown"
    | "onChange"
    | "clickableText"
    | "disabled"
    | "error"
    | "hint"
    | "iconClickableProps"
    | "leftIconClickableProps"
    | "id"
    | "placeholder"
    | "required"
    | "disable"
    | "mergeCss"
    | "min"
    | "max"
    | "css"
    | "isAnimated"
>)[];

const validForNumber = ({ altKey, ctrlKey, metaKey, key }: React.KeyboardEvent<HTMLInputElement>) => {
    return altKey || ctrlKey || metaKey || key.length > 1 || /^[0-9]$/.test(key);
};

/**
 * TextField is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextField: React.FC<TextFieldProps & HasDisablerContext> = props => {
    return (
        <ThemeConsumer>
            {(theme?: BaseTheme) => {
                const {
                    type,
                    onKeyDown,
                    onChange,
                    clickableText,
                    disable,
                    disabled,
                    error,
                    hint,
                    iconClickableProps,
                    leftIconClickableProps,
                    id,
                    placeholder,
                    required,
                    mergeCss,
                    min,
                    max,
                    isAnimated,
                    ...otherProps
                } = props;

                const isNumber = type === "number";

                const isDisabled = !!(disable || disabled);
                const inputId = id || uniqueId();
                const labelId = `${inputId}-label`;
                const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
                const descriptionId = `${inputId}-description`;
                const hasDescription = error || hint;

                const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                    { theme, ...otherProps },
                    { component: "textField", category: "formField" },
                );
                const labelProps = applyProp("labelProps");
                const sizeVariant: FormFieldSizeVariant = applyProp("sizeVariant", "default");
                const iconProps = spreadProps("iconProps");
                const leftIconProps = spreadProps("leftIconProps");
                const resolvedMergeCss = mergeCss ?? theme?.textField?.defaultProps?.mergeCss;

                const internalOnKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (isNumber && !validForNumber(event)) {
                        event.preventDefault();
                    }

                    onKeyDown?.(event);
                };

                const internalOnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
                    if (isNumber) {
                        // Sanitize: strip non-digits, remove leading zeros, enforce max 6 digits
                        let sanitized = event.currentTarget.value.replace(/[^0-9]/g, "");
                        sanitized = sanitized.replace(/^0+/, "");
                        if (sanitized.length > 6) {
                            sanitized = sanitized.substring(0, 6);
                        }
                        event.currentTarget.value = sanitized;
                    }

                    onChange?.(event);
                };

                const formFieldProps = {
                    ...iconProps,
                    size: sizeVariantValues[sizeVariant].icon,
                    color: isDisabled ? "text.disabled" : iconProps.color || "text.main",
                };

                const formIcon =
                    iconProps && Object.keys(iconProps).length > 0 ? (
                        iconClickableProps ? (
                            <FormFieldClickable
                                {...iconClickableProps}
                                disabled={isDisabled}
                                style={{ right: "0px", left: "auto" }}
                            >
                                <FormFieldIcon {...formFieldProps} style={{ right: "2px", left: "auto" }} />
                                <VisuallyHidden>{clickableText}</VisuallyHidden>
                            </FormFieldClickable>
                        ) : (
                            <FormFieldIcon {...formFieldProps} style={{ right: "2px", left: "auto" }} />
                        )
                    ) : null;

                const leftFormIcon =
                    leftIconProps && Object.keys(leftIconProps).length > 0 ? (
                        leftIconClickableProps ? (
                            <FormFieldClickable
                                {...leftIconClickableProps}
                                disabled={isDisabled}
                                style={{ left: "0px", right: "auto" }}
                            >
                                <FormFieldIcon {...leftIconProps} style={{ left: "2px", right: "auto" }} />
                                <VisuallyHidden>{clickableText}</VisuallyHidden>
                            </FormFieldClickable>
                        ) : (
                            <FormFieldIcon {...leftIconProps} style={{ left: "2px", right: "auto" }} />
                        )
                    ) : null;

                const inputStyleProps: any = {
                    disabled: isDisabled,
                    placeholder,
                    required,
                    ...inputLabelObj,
                    ...omitMultiple(otherProps, omitKeys),
                    ref: props.ref,
                    min: isNumber ? undefined : min,
                    max: isNumber ? undefined : max,
                    type: isNumber ? "text" : type || "text",
                    ...(isNumber ? { inputMode: "numeric" as const, maxLength: 6, pattern: "[0-9]*" } : {}),
                    id: inputId,
                    onKeyDown: internalOnKeyDownHandler,
                    onChange: internalOnChangeHandler,
                    "aria-labelledby": labelId,
                    "aria-describedby": hasDescription ? descriptionId : undefined,
                    "aria-invalid": error ? !!error : undefined,
                    "aria-required": !disabled && required,
                    tabIndex: 0,
                    css: applyStyledProp("css", resolvedMergeCss),
                    style: {
                        paddingRight: formIcon ? "45px" : "10px",
                        paddingLeft: leftFormIcon ? "45px" : "10px",
                    },
                };

                const InputElement = !isAnimated ? (
                    <>
                        {leftFormIcon}
                        {React.createElement(InputStyle as any, inputStyleProps)}
                        {formIcon}
                    </>
                ) : (
                    React.createElement(AnimatedTextField as any, {
                        disabled: isDisabled,
                        placeholder,
                        required,
                        ...inputLabelObj,
                        ...omitMultiple(otherProps, omitKeys),
                        min: isNumber ? undefined : min,
                        max: isNumber ? undefined : max,
                        type: isNumber ? "text" : type || "text",
                        ...(isNumber ? { inputMode: "numeric" as const, maxLength: 6, pattern: "[0-9]*" } : {}),
                        id: inputId,
                        onKeyDown: internalOnKeyDownHandler,
                        onChange: internalOnChangeHandler,
                        "aria-labelledby": labelId,
                        "aria-describedby": hasDescription ? descriptionId : undefined,
                        "aria-invalid": !!error,
                        "aria-required": !disabled && required,
                        tabIndex: 0,
                        css: applyStyledProp("css", resolvedMergeCss),
                    })
                );

                return (
                    <FormField
                        {...props}
                        labelProps={labelProps}
                        descriptionId={descriptionId}
                        formInput={InputElement}
                        labelId={labelId}
                        inputId={inputId}
                    />
                );
            }}
        </ThemeConsumer>
    );
};

TextField.displayName = "TextField";
/** @component */
export default withDisabler(TextField);
